import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { ApiError } from '../api';
import { useSession } from '../session';
import { AuthBrand, AuthIntro } from '../components/AuthBrand';

export function LoginPage() {
  const { session, login } = useSession();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(() => localStorage.getItem('kontor:tenant') ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (session) return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const key = tenant.replace(/\s+/g, '').toLowerCase();
      await login({ tenant: key, email: email.trim(), password });
      // Mandantennummer merken ist bequem und unkritisch, Passwort und Token werden nie gespeichert
      try {
        localStorage.setItem('kontor:tenant', key);
      } catch {
        // Ohne lokalen Speicher tippt man die Nummer beim nächsten Mal wieder ein
      }
      navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setError(
          e.status === 429
            ? 'Zu viele Versuche. Bitte warten Sie einige Minuten.'
            : e.status === 422
              ? Object.values(e.fields)[0] ?? e.message
              : e.message,
        );
      } else {
        setError('Die Anmeldung hat nicht geklappt.');
      }
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <AuthBrand title={<>Ihr <span className="hl">Backoffice</span></>} text="Anfragen, Kunden, Aufträge und Planung an einem Ort." />

      <main className="login__main">
        <AuthIntro eyebrow="Willkommen zurück" text="Bitte melden Sie sich in Ihrem Konto an, um fortzufahren." />
        <form className="card login__card" onSubmit={submit} noValidate>
          <h2>Anmelden</h2>
          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}
          <label className="field">
            <span>Mandantennummer</span>
            <input className="input" value={tenant} onChange={(e) => setTenant(e.target.value)} autoComplete="organization" inputMode="numeric" required placeholder="z. B. 4829173" />
            <small className="muted">Die 7-stellige Nummer Ihres Unternehmens, sie steht in Ihrer Einladung.</small>
          </label>
          <label className="field">
            <span>E-Mail</span>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus={!!tenant} />
          </label>
          <div className="field">
            <label htmlFor="password">Passwort</label>
            <div className="password">
              <input id="password" className="input" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
              <button type="button" className="password__toggle" onClick={() => setShowPassword((v) => !v)} aria-pressed={showPassword} aria-controls="password">
                {showPassword ? 'Verbergen' : 'Anzeigen'}
              </button>
            </div>
          </div>
          <button className="btn btn--primary btn--block" type="submit" disabled={busy || !tenant || !email || !password}>
            {busy ? 'Anmeldung läuft …' : 'Anmelden'}
          </button>
          <p className="muted small"><Link to="/passwort-vergessen">Passwort vergessen?</Link></p>
        </form>
      </main>
    </div>
  );
}
